import React from "react";
import ct from "class-types.macro";
import {
  BrandFeature,
  CategoryFeatureGroup,
  BrandFeatureGroup,
  UrlFeatureGroup,
  ProductFeatureGroup,
} from "~/common/Store/models/Brand";
import PhotoGridDisplay, { PhotoGridItem } from "~/common/PhotoGridDisplay";

interface Props {
  features: BrandFeature[];
  hideChildItemLinks?: boolean;
}

const ExpandedBrandFeatureGroups = ({
  features,
  hideChildItemLinks = false,
}: Props) => {
  // eslint-disable-next-line max-len
  const mapToPhotoGridItemFromFeaturedCategory = (
    category: CategoryFeatureGroup
  ): PhotoGridItem => ({
    description: category.description,
    image: category.imagePath,
    url: category.url,
    name: category.name,
    actionText: category.actionText,
    featuresPerRow: features.length,
    children: hideChildItemLinks
      ? []
      : category.categoryFeatureGroupMembers.map((grandChild) => ({
          name: grandChild.name,
          url: grandChild.url,
        })),
  });
  const mapToPhotoGridItemFromFeaturedBrand = (
    category: BrandFeatureGroup
  ): PhotoGridItem => ({
    description: category.description,
    image: category.imagePath,
    url: category.url,
    name: category.name,
    actionText: category.actionText,
    featuresPerRow: features.length,
    children: category.brandFeatureGroupMembers.map((grandChild) => ({
      name: grandChild.name,
      url: grandChild.url,
    })),
  });

  const mapToPhotoGridItemFromFeaturedUrl = (
    category: UrlFeatureGroup
  ): PhotoGridItem => ({
    description: category.description,
    image: category.imagePath,
    url: category.url,
    name: category.name,
    actionText: category.actionText,
    featuresPerRow: features.length,
    children: category.urlFeatureGroupMembers.map((grandChild) => ({
      name: grandChild.name,
      url: grandChild.url,
    })),
  });

  const mapToPhotoGridItemFromFeaturedProduct = (
    category: ProductFeatureGroup
  ): PhotoGridItem => ({
    description: category.description,
    image: category.imagePath,
    url: category.url,
    name: category.name,
    actionText: category.actionText,
    featuresPerRow: features.length,
  });

  const photoGridWrapper = ct(
    "border-gray-200",
    "border-solid",
    "border-t-0",
    "border-r-0",
    "border-l-0",
    "border-b",
    "box-border",
    "items-center",
    "justify-center",
    "-mb-px",
    "-ml-px",
    "p-5",
    "w-1/2",
    "xs:border-0",
    "xs:even:border-0",
    "xs:odd:border-0",
    "xs:p-3",
    features.length === 4 ? "lz:w-1/4" : "",
    features.length === 3 ? "lz:w-1/3" : "",
    features.length % 2 !== 0
      ? ct(
          "even:border-r",
          "first:p-6",
          "first:w-full",
          "xs:p-3",
          "xs:first:p-3",
          "xs:first:w-1/2",
          features.length === 4 ? "lz:first:w-1/4" : "",
          features.length === 3 ? "lz:first:w-1/3" : ""
        )
      : "odd:border-r"
  );

  return (
    <div className={ct("block", "w-full")}>
      <div
        className={ct(
          "border-gray-200",
          "border-solid",
          "border",
          "border-t-0",
          "box-border",
          "flex",
          "flex-wrap",
          "-mr-1/2",
          "overflow-hidden",
          "xs:border-0",
          "xs:-mr-1-1/2"
        )}
      >
        {features.map((feature) => {
          switch (feature.type) {
            case "Brand":
              return (
                <div className={photoGridWrapper}>
                  <PhotoGridDisplay
                    item={mapToPhotoGridItemFromFeaturedBrand(
                      feature.brandFeatureGroup
                    )}
                    key={`${feature.brandFeatureGroup.name}`}
                  />
                </div>
              );
            case "Category":
              return (
                <div className={photoGridWrapper}>
                  <PhotoGridDisplay
                    item={mapToPhotoGridItemFromFeaturedCategory(
                      feature.categoryFeatureGroup
                    )}
                    key={`${feature.categoryFeatureGroup.name}`}
                  />
                </div>
              );
            case "Url":
              return (
                <div className={photoGridWrapper}>
                  <PhotoGridDisplay
                    item={mapToPhotoGridItemFromFeaturedUrl(
                      feature.urlFeatureGroup
                    )}
                    key={`${feature.urlFeatureGroup.name}`}
                  />
                </div>
              );
            case "Product":
              return (
                <div className={photoGridWrapper}>
                  <PhotoGridDisplay
                    item={mapToPhotoGridItemFromFeaturedProduct(
                      feature.productFeatureGroup
                    )}
                    key={`${feature.productFeatureGroup.name}`}
                  />
                </div>
              );
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};

export default ExpandedBrandFeatureGroups;
